import React from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select } from '@components';
import { CreateAgentData } from '../../types';

interface ConfigurationTabProps {
  formData: CreateAgentData;
  onInputChange: (field: keyof CreateAgentData, value: string | number | boolean) => void;
  errors: Record<string, string>;
}

const ConfigurationTab: React.FC<ConfigurationTabProps> = ({ formData, onInputChange, errors }) => {
  const { t } = useTranslation();

  // Gemini sensitivity options
  const sensitivityOptions = [
    { value: 'high', label: t('agents.sensitivityHigh') },
    { value: 'low', label: t('agents.sensitivityLow') },
  ];

  // Activity handling options (Gemini only)
  const activityHandlingOptions = [
    { value: 'interrupts', label: t('agents.interruptionsAllowed') },
    { value: 'no_interruption', label: t('agents.noInterruption') },
  ];

  const handleBoundedNumberInput =
    (field: keyof CreateAgentData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const valueStr = e.target.value;
      if (valueStr === '') {
        onInputChange(field, '' as unknown as number);
        return;
      }

      const value = parseInt(valueStr, 10);
      if (Number.isNaN(value)) {
        return;
      }

      onInputChange(field, value);
    };

  const handleBoundedNumberBlur =
    (field: keyof CreateAgentData, fallback: number, minValue?: number, maxValue?: number) =>
    () => {
      const currentValue = formData[field];
      let finalValue: number;

      if (currentValue === '' || currentValue === null || currentValue === undefined) {
        finalValue = fallback;
      } else {
        const numValue = currentValue as number;
        finalValue = Math.min(maxValue ?? numValue, Math.max(minValue ?? 0, numValue));
      }

      onInputChange(field, finalValue);
    };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('agents.callsAndConfiguration')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          {t('agents.callsAndConfigurationDescription')}
        </p>
      </div>

      <div className="space-y-8">
        {/* Row 1: Call Duration + AI Behavior */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Call Duration Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                schedule
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.callDuration')}
              </h4>
            </div>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="max-call-time"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('agents.maxCallTimeMinutes')}
                </label>
                <Input
                  id="max-call-time"
                  type="number"
                  className="w-full"
                  value={
                    formData.max_call_time_minutes === null ||
                    formData.max_call_time_minutes === undefined
                      ? ''
                      : formData.max_call_time_minutes
                  }
                  onChange={handleBoundedNumberInput('max_call_time_minutes')}
                  onBlur={handleBoundedNumberBlur('max_call_time_minutes', 30, 1, 120)}
                  min={1}
                  max={120}
                  helperText={t('agents.maxCallTimeHelper')}
                  state={errors.max_call_time_minutes ? 'error' : 'default'}
                  errorText={errors.max_call_time_minutes}
                  rightIcon={<span className="text-sm text-gray-500 dark:text-gray-400">Min</span>}
                />
              </div>

              <div>
                <label
                  htmlFor="max-waiting-time"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                  {t('agents.maxWaitingTimeSeconds')}
                </label>
                <Input
                  id="max-waiting-time"
                  type="number"
                  className="w-full"
                  value={
                    formData.max_waiting_time_seconds === null ||
                    formData.max_waiting_time_seconds === undefined
                      ? ''
                      : formData.max_waiting_time_seconds
                  }
                  onChange={handleBoundedNumberInput('max_waiting_time_seconds')}
                  onBlur={handleBoundedNumberBlur('max_waiting_time_seconds', 15, 10, 30)}
                  min={10}
                  max={30}
                  helperText={t('agents.maxWaitingTimeHelper')}
                  state={errors.max_waiting_time_seconds ? 'error' : 'default'}
                  errorText={errors.max_waiting_time_seconds}
                  rightIcon={<span className="text-sm text-gray-500 dark:text-gray-400">Sek</span>}
                />
              </div>

              {/* Silence Check-in */}
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('agents.silenceCheckIn')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('agents.silenceCheckInDescription')}
                  </p>
                </div>
                <label
                  htmlFor="silence-check-in"
                  className="relative inline-flex items-center cursor-pointer ml-3"
                >
                  <span className="sr-only">{t('agents.silenceCheckIn')}</span>
                  <input
                    id="silence-check-in"
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.silence_check_in_enabled || false}
                    onChange={e => onInputChange('silence_check_in_enabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
                </label>
              </div>

              {formData.silence_check_in_enabled && (
                <>
                  <div>
                    <label
                      htmlFor="silence-check-in-attempts"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.silenceCheckInMaxAttempts')}
                    </label>
                    <Input
                      id="silence-check-in-attempts"
                      type="number"
                      className="w-full"
                      value={
                        formData.silence_check_in_max_attempts === null ||
                        formData.silence_check_in_max_attempts === undefined
                          ? ''
                          : formData.silence_check_in_max_attempts
                      }
                      onChange={handleBoundedNumberInput('silence_check_in_max_attempts')}
                      onBlur={handleBoundedNumberBlur('silence_check_in_max_attempts', 2, 1, 5)}
                      min={1}
                      max={5}
                      helperText={t('agents.silenceCheckInMaxAttemptsHelper')}
                      state={errors.silence_check_in_max_attempts ? 'error' : 'default'}
                      errorText={errors.silence_check_in_max_attempts}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="silence-wait-extension"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.silenceWaitExtensionSeconds')}
                    </label>
                    <Input
                      id="silence-wait-extension"
                      type="number"
                      className="w-full"
                      value={
                        formData.silence_wait_extension_seconds === null ||
                        formData.silence_wait_extension_seconds === undefined
                          ? ''
                          : formData.silence_wait_extension_seconds
                      }
                      onChange={handleBoundedNumberInput('silence_wait_extension_seconds')}
                      onBlur={handleBoundedNumberBlur('silence_wait_extension_seconds', 120, 30, 600)}
                      min={30}
                      max={600}
                      helperText={t('agents.silenceWaitExtensionHelper')}
                      state={errors.silence_wait_extension_seconds ? 'error' : 'default'}
                      errorText={errors.silence_wait_extension_seconds}
                      rightIcon={
                        <span className="text-sm text-gray-500 dark:text-gray-400">Sek</span>
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* AI Behavior Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                psychology
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.aiBehavior')}
              </h4>
            </div>
            <div className="space-y-4">
              {/* Creativity Slider — Both providers */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.responseCreativity')}: {formData.creativity ?? 30}
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  value={formData.creativity ?? 30}
                  onChange={e => onInputChange('creativity', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${formData.creativity ?? 30}%, #e5e7eb ${formData.creativity ?? 30}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>0 ({t('agents.conservative')})</span>
                  <span>{t('agents.balanced')}</span>
                  <span>100 ({t('agents.creative')})</span>
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {t('agents.creativityDescriptionGemini')}
                </p>
              </div>

              {/* Proactive Audio & Affective Dialog: unter gemini-3.8-live immer
                  aktiv (Backend gated die Flags weg) → keine UI-Toggles mehr.
                  Datenfelder bleiben (Default true) für 2.5-Rollback erhalten. */}

              {/* Caller Memory */}
              <div className="flex items-center justify-between p-3 bg-white dark:bg-gray-700/50 rounded-lg">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('agents.callerMemory.enable')}
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('agents.callerMemory.enableDescription')}
                  </p>
                </div>
                <label
                  htmlFor="caller-memory-enabled"
                  className="relative inline-flex items-center cursor-pointer ml-3"
                >
                  <span className="sr-only">{t('agents.callerMemory.enable')}</span>
                  <input
                    id="caller-memory-enabled"
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.caller_memory_enabled || false}
                    onChange={e => onInputChange('caller_memory_enabled', e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-checked:bg-primary-600 rounded-full peer transition-colors peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600"></div>
                </label>
              </div>

              {formData.caller_memory_enabled && (
                <>
                  <div>
                    <label
                      htmlFor="caller-memory-greeting-mode"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.callerMemory.greetingMode')}
                    </label>
                    <Select
                      id="caller-memory-greeting-mode"
                      className="w-full"
                      value={formData.caller_memory_greeting_mode || 'passive'}
                      onChange={value =>
                        onInputChange('caller_memory_greeting_mode', value as string)
                      }
                      options={[
                        { value: 'passive', label: t('agents.callerMemory.greetingModePassive') },
                        { value: 'active', label: t('agents.callerMemory.greetingModeActive') },
                      ]}
                      helperText={t('agents.callerMemory.greetingModeHelper')}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="caller-memory-retention"
                      className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                    >
                      {t('agents.callerMemory.retentionDays')}
                    </label>
                    <Input
                      id="caller-memory-retention"
                      type="number"
                      className="w-full"
                      value={
                        formData.caller_memory_retention_days === null ||
                        formData.caller_memory_retention_days === undefined
                          ? ''
                          : formData.caller_memory_retention_days
                      }
                      onChange={handleBoundedNumberInput('caller_memory_retention_days')}
                      onBlur={handleBoundedNumberBlur('caller_memory_retention_days', 90, 1, 360)}
                      min={1}
                      max={360}
                      helperText={t('agents.callerMemory.retentionDaysHelper')}
                      state={errors.caller_memory_retention_days ? 'error' : 'default'}
                      errorText={errors.caller_memory_retention_days}
                      rightIcon={
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {t('agents.callerMemory.days')}
                        </span>
                      }
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Row 2: Voice Activity Detection + Timing Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Voice Activity Detection Settings */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                hearing
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.voiceActivityDetection')}
              </h4>
            </div>
            <div className="space-y-4">
              {/* Gemini Speech Sensitivity Dropdowns */}
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('agents.startOfSpeechSensitivity')}
                  </label>
                  <Select
                    options={sensitivityOptions}
                    value={formData.start_of_speech_sensitivity || 'low'}
                    onChange={value =>
                      onInputChange('start_of_speech_sensitivity', value as string)
                    }
                    helperText={t('agents.startOfSpeechDescription')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('agents.endOfSpeechSensitivity')}
                  </label>
                  <Select
                    options={sensitivityOptions}
                    value={formData.end_of_speech_sensitivity || 'low'}
                    onChange={value => onInputChange('end_of_speech_sensitivity', value as string)}
                    helperText={t('agents.endOfSpeechDescription')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('agents.activityHandling')}
                  </label>
                  <Select
                    options={activityHandlingOptions}
                    value={formData.activity_handling || 'interrupts'}
                    onChange={value => onInputChange('activity_handling', value as string)}
                    helperText={t('agents.activityHandlingDescription')}
                  />
                </div>
              </>
            </div>
          </div>

          {/* Timing Configuration */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                timer
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.timingConfiguration')}
              </h4>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.prefixPadding')}: {formData.prefix_padding || 350}ms
                </label>
                <input
                  type="range"
                  min="200"
                  max="1000"
                  step="50"
                  value={formData.prefix_padding || 350}
                  onChange={e => onInputChange('prefix_padding', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((formData.prefix_padding || 350) - 200) / (1000 - 200)) * 100}%, #e5e7eb ${(((formData.prefix_padding || 350) - 200) / (1000 - 200)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>200ms</span>
                  <span>{t('agents.bufferTimeBeforeProcessing')}</span>
                  <span>1000ms</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('agents.silenceDetection')}: {formData.silence || 400}ms
                </label>
                <input
                  type="range"
                  min="200"
                  max="2000"
                  step="50"
                  value={formData.silence || 400}
                  onChange={e => onInputChange('silence', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${(((formData.silence || 400) - 200) / (2000 - 200)) * 100}%, #e5e7eb ${(((formData.silence || 400) - 200) / (2000 - 200)) * 100}%, #e5e7eb 100%)`,
                  }}
                />
                <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
                  <span>200ms</span>
                  <span>{t('agents.durationOfSilenceBeforeEnd')}</span>
                  <span>2000ms</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Row 3: SMS Settings (central sender name) — left half column */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-6">
            <div className="flex items-center mb-4">
              <span className="material-icons text-primary-500 dark:text-primary-300 mr-2">
                sms
              </span>
              <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                {t('agents.smsSettings')}
              </h4>
            </div>
            <div>
              <label
                htmlFor="sms-sender-name"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('agents.smsSenderName')}
              </label>
              <Input
                id="sms-sender-name"
                type="text"
                className="w-full"
                maxLength={11}
                placeholder={t('agents.smsSenderNamePlaceholder')}
                value={formData.sms_sender_name || ''}
                onChange={e => onInputChange('sms_sender_name', e.target.value)}
                helperText={t('agents.smsSenderNameHint')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { ConfigurationTab };
